import React, { useState, useEffect, useRef, useContext } from 'react'
import { useHistory } from 'react-router-dom'

import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import Select from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import { Grid, ListItemText, Typography, makeStyles } from '@material-ui/core'

import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import CircularProgress from '@material-ui/core/CircularProgress'

import { i18n } from '../../translate/i18n'
import api from '../../services/api'
import ButtonWithSpinner from '../ButtonWithSpinner'
import toastError from '../../errors/toastError'
import useQueues from '../../hooks/useQueues'
import { AuthContext } from '../../context/Auth/AuthContext'

const useStyles = makeStyles(theme => ({
  maxWidth: {
    width: '100%',
  },
}))

const isGroupQueue = queue => queue?.name?.trim().toLowerCase() === 'grupos'

const findDefaultQueueId = queues => {
  if (!Array.isArray(queues) || queues.length === 0) return ''

  const firstNonGroupQueue = queues.find(queue => !isGroupQueue(queue))

  return (firstNonGroupQueue || queues[0])?.id || ''
}

const findDefaultWhatsappId = (whatsapps, preferredId) => {
  if (preferredId && Array.isArray(whatsapps) && whatsapps.some(whatsapp => whatsapp.id === preferredId)) {
    return preferredId
  }

  if (Array.isArray(whatsapps) && whatsapps.length > 0) {
    return whatsapps[0].id
  }

  return ''
}

const filterOptions = createFilterOptions({
  trim: true,
})

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid }) => {
  const history = useHistory()
  const [options, setOptions] = useState([])
  const [queues, setQueues] = useState([])
  const [allQueues, setAllQueues] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedQueue, setSelectedQueue] = useState('')
  const classes = useStyles()
  const { findAll: findAllQueues } = useQueues()
  const isMounted = useRef(true)
  const [whatsapps, setWhatsapps] = useState([])
  const [selectedWhatsapp, setSelectedWhatsapp] = useState('')
  const [ticketWhatsappId, setTicketWhatsappId] = useState(null)
  const [hasUserSelectedWhatsapp, setHasUserSelectedWhatsapp] = useState(false)
  const { user } = useContext(AuthContext)
  const { companyId, whatsappId } = user

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!modalOpen) return

    let isActive = true
    setLoading(true)

    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`/whatsapp`, { params: { companyId, session: 0 } })

          if (!isMounted.current || !isActive) return

          setWhatsapps(data)
        } catch (err) {
          if (!isMounted.current || !isActive) return
          toastError(err)
          setWhatsapps([])
          setSelectedWhatsapp('')
        } finally {
          if (isMounted.current && isActive) {
            setLoading(false)
          }
        }
      }

      fetchContacts()
    }, 500)

    return () => {
      isActive = false
      clearTimeout(delayDebounceFn)
      if (isMounted.current) {
        setLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, companyId, whatsappId])

  useEffect(() => {
    if (!modalOpen) {
      setTicketWhatsappId(null)
      setHasUserSelectedWhatsapp(false)
      return
    }

    if (!ticketid) {
      setTicketWhatsappId(null)
      return
    }

    let isActive = true

    const fetchTicket = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketid}`)
        if (!isActive) return
        setTicketWhatsappId(data?.whatsappId ?? null)
      } catch (err) {
        if (!isActive) return
        toastError(err)
        setTicketWhatsappId(null)
      }
    }

    fetchTicket()

    return () => {
      isActive = false
    }
  }, [modalOpen, ticketid])

  useEffect(() => {
    if (!modalOpen || hasUserSelectedWhatsapp) return

    if (!Array.isArray(whatsapps) || whatsapps.length === 0) {
      setSelectedWhatsapp('')
      return
    }

    setSelectedWhatsapp(findDefaultWhatsappId(whatsapps, ticketWhatsappId || whatsappId))
  }, [modalOpen, hasUserSelectedWhatsapp, whatsapps, ticketWhatsappId, whatsappId])

  useEffect(() => {
    if (!modalOpen || !isMounted.current) return

    const loadQueues = async () => {
      const list = await findAllQueues()

      if (!isMounted.current) return

      setAllQueues(list)
      setQueues(list)

      if (list.length > 0) {
        const userQueues = Array.isArray(user?.queues) ? user.queues : []
        const userPreferredQueueId = findDefaultQueueId(userQueues)

        if (userPreferredQueueId) {
          const userQueueInList = list.find(queue => queue.id === userPreferredQueueId && !isGroupQueue(queue))
          if (userQueueInList) {
            setSelectedQueue(userQueueInList.id)
            return
          }
        }

        setSelectedQueue(findDefaultQueueId(list))
      } else {
        setSelectedQueue('')
      }
    }

    loadQueues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false)
      return
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true)
      const fetchUsers = async () => {
        try {
          const { data } = await api.get('/users/', {
            params: { searchParam },
          })
          setOptions(data.users)
          setLoading(false)
        } catch (err) {
          setLoading(false)
          toastError(err)
        }
      }

      fetchUsers()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, modalOpen])

  const handleClose = () => {
    onClose()
    setSearchParam('')
    setSelectedUser(null)
    setQueues(allQueues)
    setSelectedQueue(findDefaultQueueId(allQueues))
    setHasUserSelectedWhatsapp(false)
    setSelectedWhatsapp(findDefaultWhatsappId(whatsapps, ticketWhatsappId || whatsappId))
  }

  const handleSaveTicket = async e => {
    e.preventDefault()
    if (!ticketid) return
    if (!selectedUser && (!selectedQueue || selectedQueue === '')) return
    setLoading(true)
    try {
      let data = {}

      if (selectedUser) {
        data.userId = selectedUser.id
      }

      if (selectedQueue && selectedQueue !== '') {
        data.queueId = selectedQueue

        if (!selectedUser) {
          data.status = 'pending'
          data.userId = null
        }
      }

      if (selectedWhatsapp) {
        data.whatsappId = selectedWhatsapp
      }
      const { data: updated } = await api.put(`/tickets/${ticketid}`, data)
      document.dispatchEvent(new CustomEvent('ticket-updated', { detail: updated }))

      history.push(`/tickets`)
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
  }

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth='lg' scroll='paper'>
      <form onSubmit={handleSaveTicket}>
        <DialogTitle id='form-dialog-title'>{i18n.t('transferTicketModal.title')}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            style={{ width: 300, marginBottom: 20 }}
            getOptionLabel={option => `${option.name}`}
            onChange={(e, newValue) => {
              setSelectedUser(newValue)
              if (newValue != null && Array.isArray(newValue.queues) && newValue.queues.length > 0) {
                setQueues(newValue.queues)
                setSelectedQueue(findDefaultQueueId(newValue.queues))
              } else {
                setQueues(allQueues)
                setSelectedQueue(findDefaultQueueId(allQueues))
              }
            }}
            options={options}
            filterOptions={filterOptions}
            freeSolo
            autoHighlight
            noOptionsText={i18n.t('transferTicketModal.noOptions')}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t('transferTicketModal.fieldLabel')}
                variant='outlined'
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <FormControl variant='outlined' className={classes.maxWidth}>
            <InputLabel>{i18n.t('transferTicketModal.fieldQueueLabel')}</InputLabel>
            <Select
              value={selectedQueue}
              onChange={e => setSelectedQueue(e.target.value)}
              label={i18n.t('transferTicketModal.fieldQueuePlaceholder')}
              renderValue={value => {
                const queue = queues.find(q => q.id === value)
                return value ? <span translate='no'>{queue?.name || ''}</span> : ''
              }}
            >
              {queues.map(queue => (
                <MenuItem key={queue.id} value={queue.id} translate='no' className='notranslate'>
                  <span translate='no'>{queue.name}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* CONEXAO */}
          <Grid container spacing={2} style={{ marginTop: '15px' }}>
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant='outlined'
                value={selectedWhatsapp}
                onChange={e => {
                  setSelectedWhatsapp(e.target.value)
                  setHasUserSelectedWhatsapp(true)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === '') {
                    return 'Selecione uma Conexão'
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                  return <span translate='no'>{whatsapp?.name || ''}</span>
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id} translate='no' className='notranslate'>
                      <ListItemText
                        primary={
                          <>
                            {/* {IconChannel(whatsapp.channel)} */}
                            <Typography component='span' style={{ fontSize: 14, marginLeft: '10px', display: 'inline-flex', alignItems: 'center', lineHeight: '2' }}>
                              <span translate='no'>{whatsapp.name}</span>&nbsp;
                              <p className={whatsapp.status === 'CONNECTED' ? classes.online : classes.offline}>({whatsapp.status})</p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='secondary' disabled={loading} variant='outlined'>
            {i18n.t('transferTicketModal.buttons.cancel')}
          </Button>
          <ButtonWithSpinner variant='contained' type='submit' color='primary' loading={loading}>
            {i18n.t('transferTicketModal.buttons.ok')}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default TransferTicketModalCustom
